import { useState, useEffect } from 'react';
import { DollarSign, Lock, Unlock, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface Hold {
  id: string;
  amount: number;
  status: 'active' | 'released';
  createdAt: string;
}

export default function MoovPayouts() {
  const [holds, setHolds] = useState<Hold[]>([]);
  const [moovStatus, setMoovStatus] = useState<string>('disconnected');

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const data = await apiFetch<{ status: string }>('/api/moov/status');
        setMoovStatus(data.status);
      } catch (error) {
        console.error('Failed to check Moov status:', error);
      }
    };
    
    checkStatus();
  }, []);

  const placeHold = async (amount: number) => {
    try {
      const hold = await apiFetch<Omit<Hold, 'createdAt'>>('/api/moov/hold', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: 'demo-account', amount }),
      });

      setHolds([...holds, { ...hold, createdAt: new Date().toISOString() }]);
    } catch (error) {
      console.error('Failed to place hold:', error);
    }
  };

  const releaseHold = async (holdId: string) => {
    try {
      await fetch('/api/moov/release', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ holdId }),
      });
      
      setHolds(holds.map(h => h.id === holdId ? { ...h, status: 'released' as const } : h));
    } catch (error) {
      console.error('Failed to release hold:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Moov Payments</h3>
              <p className="text-sm text-slate-400">
                Status: <span className={moovStatus === 'active' ? 'text-emerald-400' : 'text-slate-400'}>
                  {moovStatus}
                </span>
              </p>
            </div>
          </div>
        </div>

        {holds.length === 0 ? (
          <p className="text-sm text-slate-500">No active holds</p>
        ) : (
          <div className="space-y-2">
            {holds.map((hold) => (
              <div
                key={hold.id}
                className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3 border border-slate-700/30"
              >
                <div className="flex items-center gap-3">
                  {hold.status === 'active' ? (
                    <Lock className="w-4 h-4 text-orange-400" />
                  ) : (
                    <Unlock className="w-4 h-4 text-emerald-400" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-white">
                      ${hold.amount.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(hold.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                {hold.status === 'active' ? (
                  <button
                    onClick={() => releaseHold(hold.id)}
                    className="text-xs bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-3 py-1 rounded transition-all"
                  >
                    Release
                  </button>
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => placeHold(100)}
        className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-purple-600 hover:to-pink-700 transition-all"
      >
        Place Demo Hold ($100)
      </button>
    </div>
  );
}
